import { Edit, Info, MoreHoriz } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { formatDate } from '~/lib/datetime';
import type { NetworkTvBrands__TvBrand as TVBrand } from '../../queries/list.generated';
import { Menu } from '../components';

export const useColumns = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo<GridColDef<TVBrand>[]>(
    () => [
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: isSmallAndUp ? 180 : 0,
      },
      {
        field: 'kind',
        ...hiddenHeader(t('common.labels.type')),
        sortable: false,
        renderCell: ({ row }) => {
          return (
            !row.canUpdate.value && (
              <Tooltip title={t('pages.tv_brands.list.columns.system_tooltip')}>
                <Chip label={t('pages.tv_brands.list.columns.system')} color="primary" />
              </Tooltip>
            )
          );
        },
        minWidth: 150,
      },
      {
        field: 'createdAt',
        headerName: t('common.labels.created'),
        renderCell: ({ row }) => {
          return formatDate(row.createdAt);
        },
        minWidth: 250,
      },
      {
        field: 'devices',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) => {
          return <Chip color="primary" label={row.deviceCount || 0} />;
        },
        minWidth: 100,
      },
      {
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }) => {
          const canUpdate = row.canUpdate.value;
          return [
            <ActionButtonLink
              key={`view-edit-${row.id}`}
              title={t(canUpdate ? 'common.buttons.edit' : 'common.actions.view')}
              Icon={canUpdate ? Edit : Info}
              to={`${row.id}`}
            />,
            <Menu key={`more-action-${row.id}`} tvBrand={row} Icon={MoreHoriz} />,
          ];
        },
      },
    ],
    [isSmallAndUp, t],
  );
};
